import React, { useCallback, useEffect, useMemo, useState } from "react";

import { IconButton } from "@mui/material";
import MuiTableRow from "@mui/material/TableRow";
import { capitalize } from "lodash";
import { useParams } from "react-router-dom";

import useDeleteTransitionProposalHolding from "@fartherfinance/frontend/api/TransitionProposals/hooks/useDeleteTransitionProposalHolding";
import usePatchTransitionProposalHolding from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePatchTransitionProposalHolding";
import usePostTransitionProposalHolding from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostTransitionProposalHolding";
import {
  OcrAccountHoldingId,
  TransitionProposalHoldingsFilterResponse,
  TransitionProposalId,
  TransitionProposalTableHolding,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import { randomString } from "@fartherfinance/frontend/api/utils/randomString";

import { initialTransitionProposalFlowState } from "../../../constants";
import { getHoldingsTypeString } from "../../../helpers";
import { TransitionProposalFlowStateData } from "../../../types";
import { columns } from "../constants";
import { useFilterTypeData } from "../hooks/useGetFilterTypeData";
import strings from "../strings";
import {
  Field,
  FieldKey,
  FilterName,
  FormData,
  SelectedFilter,
} from "../types";
import Arrow from "@src/assets/svg/components/Arrow";
import FilterList from "@src/assets/svg/components/Filter";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Chip from "@src/sharedComponents/Chip/Chip";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import CustomDropdownPopoverBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownPopoverBase";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import ScrollContainer from "@src/sharedComponents/ScrollContainer/ScrollContainer";
import SearchInput from "@src/sharedComponents/SearchInput/SearchInput";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import {
  FartherTable,
  FartherTableBody,
  FartherTableBodyCell,
  FartherTableContainer,
  FartherTableHead,
  FartherTableHeaderCell,
} from "@src/sharedComponents/Table/Components";
import FormTableRow from "@src/sharedComponents/Table/FormTableRow/FormTableRow";
import { TableSortOrder } from "@src/sharedComponents/Table/Types";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import FormRowCell from "./FormRowCell/FormRowCell";
import HoldingFilterCheckbox from "./HoldingFilterCheckbox/HoldingFilterCheckbox";
import RangeSlider from "./RangeSlider/RangeSlider";

import styles from "./EditableTransitionProposalHoldingsTable.module.css";

interface Props {
  holdings: TransitionProposalTableHolding[];
  onSort: (sortBy: string) => void;
  sortOrder: TableSortOrder;
  filters: TransitionProposalHoldingsFilterResponse;
  isLoading?: boolean;
}

const EditableTransitionProposalHoldingsTable: React.FC<Props> = ({
  holdings,
  onSort,
  sortOrder,
  filters,
  isLoading,
}) => {
  const { transitionProposalId } = useParams<{
    transitionProposalId: TransitionProposalId;
  }>();
  const [formData, setFormData] = useState<FormData[]>([]);

  const {
    state: {
      data: {
        holdingsFilters: holdingsFiltersState,
        filterSearchTerm,
        appliedFilter,
        documentPreviewFilterClicked,
      },
    },
    dispatch,
  } = useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const activeFilters = useMemo(() => {
    if (holdingsFiltersState === undefined) {
      return initialTransitionProposalFlowState.data.holdingsFilters;
    }
    return holdingsFiltersState;
  }, [holdingsFiltersState]);

  const { checkBoxDropdownFilterList, sliderDropdownFilterRange } =
    useFilterTypeData(filters, appliedFilter, filterSearchTerm, activeFilters);

  const auth = useRequestAuth();

  const notify = useStatusNotification();

  const {
    mutateAsync: createTransitionProposalHolding,
    isLoading: isMutatingPostProposalHolding,
  } = usePostTransitionProposalHolding(auth, transitionProposalId);

  const { mutateAsync: updateTransitionProposalHolding } =
    usePatchTransitionProposalHolding(auth);

  const {
    mutateAsync: deleteTransitionProposalHolding,
    isLoading: isDeletingProposalHolding,
  } = useDeleteTransitionProposalHolding(auth);

  useEffect(() => {
    const formattedHoldings = holdings.map((holding) => ({
      accountHoldingId: holding.accountHoldingId,
      fields: {
        ...holding.fields,
        type: getHoldingsTypeString(holding.fields.type),
      },
    }));

    setFormData([...formattedHoldings]);
  }, [holdings]);

  const handleOnUpdateTransitionProposalHolding = useCallback(
    async (accountHoldingId: OcrAccountHoldingId, fields: Field) => {
      try {
        await updateTransitionProposalHolding({
          body: fields,
          accountHoldingId,
          transitionProposalId,
        });
        notify(strings.HOLDINGS_UPDATED, "Success");
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_UPDATING_HOLDINGS,
          genericErrorMessage: strings.ERROR_UPDATING_HOLDINGS_GENERIC,
          notify,
        });
      }
    },
    [updateTransitionProposalHolding, transitionProposalId, notify]
  );

  const handleOnDeleteExistingHoldingRow = useCallback(
    async (accountHoldingId: OcrAccountHoldingId) => {
      try {
        await deleteTransitionProposalHolding({
          accountHoldingId,
          transitionProposalId,
        });
        notify(strings.HOLDINGS_DELETED, "Success");
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_DELETING_HOLDINGS,
          genericErrorMessage: strings.ERROR_DELETING_HOLDINGS_GENERIC,
          notify,
        });
      }
    },
    [deleteTransitionProposalHolding, notify, transitionProposalId]
  );

  const handleOnDeleteRow = useCallback(
    (row: FormData) => async (formName: string) => {
      const isNewRow = row.accountHoldingId.startsWith("TEMP_");

      if (isNewRow) {
        setFormData((prev) =>
          prev.filter((data) => `form-${data.accountHoldingId}` !== formName)
        );
      } else {
        await handleOnDeleteExistingHoldingRow(row.accountHoldingId);
        if (formData.length === 1) {
          dispatch({
            type: actions.SET_DATA,
            payload: {
              holdingsFilters: {
                ...initialTransitionProposalFlowState.data.holdingsFilters,
              },
            },
          });
        }
      }
    },
    [handleOnDeleteExistingHoldingRow, dispatch, formData]
  );

  const handleOnCreateTransitionProposalHolding = useCallback(
    async (fields: Field) => {
      try {
        await createTransitionProposalHolding({
          body: fields,
          transitionProposalId,
        });
        notify(strings.HOLDINGS_CREATED, "Success");
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_CREATING_HOLDINGS,
          genericErrorMessage: strings.ERROR_CREATING_HOLDINGS_GENERIC,
          notify,
        });
      }
    },
    [createTransitionProposalHolding, notify, transitionProposalId]
  );

  const handleSubmit = useCallback(
    (accountHoldingId: OcrAccountHoldingId) =>
      async (_: string, formValues: Field) => {
        const isNewRow = accountHoldingId.startsWith("TEMP_");

        const cleanFieldValue = (key: string, value: any): any => {
          if (value === "--" || value === "") {
            return null;
          }
          if (key === "type" && typeof value === "string") {
            return value.toLowerCase().trim();
          }
          return value.trim();
        };

        const body = Object.fromEntries(
          Object.entries(formValues).map(([key, value]) => [
            key,
            cleanFieldValue(key, value),
          ])
        ) as Field;

        if (isNewRow) {
          await handleOnCreateTransitionProposalHolding(body);
        } else {
          await handleOnUpdateTransitionProposalHolding(accountHoldingId, body);
        }
      },
    [
      handleOnCreateTransitionProposalHolding,
      handleOnUpdateTransitionProposalHolding,
    ]
  );

  const generateTempId = () =>
    `TEMP_${randomString(4)}-${randomString(4)}-${randomString(4)}`;

  const handleOnAddRow = useCallback(() => {
    setFormData((prev) => [
      {
        accountHoldingId: generateTempId() as OcrAccountHoldingId,
        fields: {
          name: null,
          ticker: null,
          quantity: null,
          costBasis: null,
          price: null,
          marketValue: null,
          unrealizedGainOrLoss: null,
          percentOfAccount: null,
          type: null,
          fileName: null,
          accountName: null,
          statementPeriod: null,
        },
      },
      ...prev,
    ]);
  }, []);

  const handleOnSort = (sortBy: string) => () => onSort(sortBy);

  const handleSetCheckedFilters = ({
    filterName,
    value,
    checked,
  }: SelectedFilter) => {
    if (checked) {
      const existingFilters = activeFilters[filterName];

      const filterKeys = Object.keys(activeFilters) as FilterName[];

      const filterToClear = filterKeys.find(
        (key) => key !== filterName && activeFilters[key].length > 0
      ) as FilterName;

      const selectedFilter = { [filterName]: [...existingFilters, value] };

      dispatch({
        type: actions.SET_DATA,
        payload: {
          appliedFilter: filterName,
          documentPreviewFilterClicked: false,
          holdingsFilters: {
            ...activeFilters,
            ...selectedFilter,
            [filterToClear]: [],
          },
        },
      });
    } else {
      dispatch({
        type: actions.SET_DATA,
        payload: {
          appliedFilter: filterName,
          documentPreviewFilterClicked: false,
          holdingsFilters: {
            ...activeFilters,
            [filterName]: activeFilters[filterName].filter(
              (item) => item !== value
            ),
          },
        },
      });
    }
  };

  const handleSetRangeFilters = (
    _event: Event | React.SyntheticEvent,
    value: number | number[]
  ) => {
    if (!Array.isArray(value) || value.length !== 2) {
      return;
    }

    if (!appliedFilter) {
      return;
    }

    dispatch({
      type: actions.SET_DATA,
      payload: {
        appliedFilter,
        documentPreviewFilterClicked: false,
        holdingsFilters: {
          ...activeFilters,
          [appliedFilter]: value,
        },
      },
    });
  };

  const handleSetPopover = (filterName: FilterName | undefined) => () => {
    dispatch({
      type: actions.SET_DATA,
      payload: {
        appliedFilter: filterName,
        documentPreviewFilterClicked: false,
        filterSearchTerm: "",
      },
    });
  };

  const onHandleSearchInput = (input: string) => {
    dispatch({
      type: actions.SET_DATA,
      payload: {
        filterSearchTerm: input,
        documentPreviewFilterClicked: false,
        appliedFilter,
      },
    });
  };

  const handleOnClearFilters = (filterName: FilterName) => () => {
    onHandleSearchInput("");
    dispatch({
      type: actions.SET_DATA,
      payload: {
        appliedFilter: filterName,
        documentPreviewFilterClicked: false,
        holdingsFilters: {
          ...activeFilters,
          [filterName]: [],
        },
      },
    });
  };

  const showLoadingState =
    isMutatingPostProposalHolding || isDeletingProposalHolding;

  const sanitizeDefaultValues = (defaults: Field | undefined) => {
    if (!defaults) {
      return defaults;
    }
    const sanitizedEntries = Object.entries(defaults).map(([key, value]) => [
      key,
      value === null ? "" : value,
    ]);

    return Object.fromEntries(sanitizedEntries);
  };

  return (
    <>
      <FartherTableContainer className={styles.addHoldingsWrapper}>
        <div className={styles.addHoldingsButtonContainer}>
          <Button
            variant="outlined"
            buttonType="primary"
            text="Add Holdings"
            onClick={handleOnAddRow}
          />
        </div>
      </FartherTableContainer>
      <FartherTableContainer className={styles.tableContainer}>
        <FartherTable className={styles.table}>
          <FartherTableHead>
            {columns.map((header) => (
              <FartherTableHeaderCell
                key={header.value}
                style={{
                  width: header.width,
                  textAlign: header.value === "actions" ? "center" : "left",
                }}
                className={toClassName(styles.headerCell, {
                  [styles.stickyHeaderCell]: header.value === "actions",
                })}
              >
                <div
                  className={toClassName(styles.headerCellContent, {
                    [styles.actionHeaderCell]: header.value === "actions",
                  })}
                >
                  {header.label}
                  {header.value !== "actions" && (
                    <div
                      className={toClassName(styles.headerCellIcons, {
                        [styles.headerCellIconsActive]:
                          activeFilters !== undefined
                            ? (activeFilters[
                                header.value as keyof typeof activeFilters
                              ]?.length ?? 0) > 0 ||
                              header.value === appliedFilter
                            : false,
                      })}
                    >
                      <IconButton
                        onClick={handleOnSort(header.value)}
                        className={styles.iconButton}
                      >
                        <Arrow
                          rotate="bottom"
                          className={toClassName({
                            [styles.Arrow]: sortOrder === "desc",
                            [styles.arrowUp]: sortOrder === "asc",
                          })}
                        />
                      </IconButton>

                      <CustomDropdownPopoverBase
                        popoverElement={
                          <IconButton
                            className={styles.iconButton}
                            onClick={handleSetPopover(header.value)}
                          >
                            <FilterList />
                          </IconButton>
                        }
                        closeDropdown={handleSetPopover(undefined)}
                        showDropdown={
                          !documentPreviewFilterClicked &&
                          appliedFilter === header.value
                        }
                        menuWidth={240}
                      >
                        <Conditional condition={header.inputType === "text"}>
                          <SearchInput
                            searchInput={filterSearchTerm ?? ""}
                            setSearchInput={onHandleSearchInput}
                            placeholder="Search"
                            className={styles.filterSearchTerm}
                          />
                        </Conditional>
                        <Conditional
                          condition={
                            activeFilters[header.value].length > 0 &&
                            header.inputType === "text"
                          }
                        >
                          <div className={styles.clearFilterContainer}>
                            <Chip
                              className={styles.clearFilterChip}
                              onClick={handleOnClearFilters(header.value)}
                              label={"Clear All"}
                            />
                          </div>
                        </Conditional>
                        <ScrollContainer maxHeight={300}>
                          <Conditional
                            condition={
                              checkBoxDropdownFilterList.length > 0 &&
                              header.inputType === "text"
                            }
                          >
                            {checkBoxDropdownFilterList.map(
                              (value: string, index) => (
                                <HoldingFilterCheckbox
                                  key={`checkbox-${value}-${index}`}
                                  value={value}
                                  appliedFilter={appliedFilter}
                                  checkedFilters={activeFilters}
                                  onFilter={handleSetCheckedFilters}
                                />
                              )
                            )}
                          </Conditional>
                          <Conditional
                            condition={
                              checkBoxDropdownFilterList.length === 0 &&
                              header.inputType === "text" &&
                              !isLoading
                            }
                          >
                            <Text
                              className={styles.noResultsDropdownText}
                              variant="body"
                              color="text"
                            >
                              No Results
                            </Text>
                          </Conditional>
                          <Conditional
                            condition={
                              sliderDropdownFilterRange !== undefined &&
                              header.inputType === "number"
                            }
                          >
                            <div className={styles.sliderContainer}>
                              <Text variant="title2">
                                {capitalize(header.label)}
                              </Text>
                              <RangeSlider
                                startAdornment={
                                  header.value !== "percentOfAccount"
                                    ? header.adornment
                                    : ""
                                }
                                endAdornment={
                                  header.value === "percentOfAccount" ? "%" : ""
                                }
                                min={sliderDropdownFilterRange.min}
                                max={sliderDropdownFilterRange.max}
                                value={sliderDropdownFilterRange.value}
                                onChangeCommitted={handleSetRangeFilters}
                              />
                              <div
                                className={styles.clearSliderFilterContainer}
                              >
                                <Chip
                                  className={styles.clearFilterChip}
                                  onClick={handleOnClearFilters(header.value)}
                                  label={"Clear"}
                                />
                              </div>
                            </div>
                          </Conditional>
                        </ScrollContainer>
                      </CustomDropdownPopoverBase>
                    </div>
                  )}
                </div>
              </FartherTableHeaderCell>
            ))}
          </FartherTableHead>
          <FartherTableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <MuiTableRow key={`skeleton-row-${index}`}>
                  {columns.map((_, colIndex) => (
                    <FartherTableBodyCell
                      key={colIndex}
                      className={styles.fixedCellHeight}
                    >
                      <Skeleton />
                    </FartherTableBodyCell>
                  ))}
                </MuiTableRow>
              ))
            ) : formData.length > 0 ? (
              formData.map((row) => (
                <FormTableRow<Field>
                  key={row.accountHoldingId}
                  className={styles.tableRow}
                  formName={`form-${row.accountHoldingId}`}
                  onSubmit={handleSubmit(row.accountHoldingId)}
                  onDeleteRow={handleOnDeleteRow(row)}
                  defaultValues={sanitizeDefaultValues(row.fields)}
                >
                  {Object.keys(row.fields).map((key) => (
                    <FormRowCell
                      key={key}
                      column={columns.find((column) => column.value === key)!}
                      defaultValue={row.fields[key as FieldKey] ?? "--"}
                      isLoading={isLoading}
                    />
                  ))}
                </FormTableRow>
              ))
            ) : (
              <MuiTableRow>
                <FartherTableBodyCell
                  colSpan={columns.length}
                  className={styles.noDataCell}
                >
                  No data available
                </FartherTableBodyCell>
              </MuiTableRow>
            )}
          </FartherTableBody>
        </FartherTable>
        {showLoadingState && <LogoLoadingStill onTop />}
      </FartherTableContainer>
    </>
  );
};

export default EditableTransitionProposalHoldingsTable;
